import { timerHelperModule } from '../../../../shared/app/helpers/timer-helper';
import { modalComponentModule } from '../../../../shared/app/components/modal/modal';
import { appAlertComponentModule } from '../../../../shared/app/components/app-alert/app-alert';
import challengeSubmittingDialogTemplate from './challenge-submitting-dialog.tpl.html';
import './challenge-submitting-dialog.less';
import { busyIndicatorComponentModule } from '../../../../shared/app/components/busy-indicator/busy-indicator';
import { trackerServiceModule } from '../../../../shared/app/services/tracker-service/tracker-service';
import {
  ERROR_CONTEXT,
  messageHubServiceModule,
} from '../../services/message-hub-service/message-hub-service';

export const MASTRA_BASE_URL = window.location.host.includes('localhost')
  ? 'http://localhost:4111/v1'
  : '/mastra/v1';

export const challengeSubmittingDialogComponentModule = angular
  .module('Q.components.challenge-submitting-dialog', [
    timerHelperModule,
    modalComponentModule,
    appAlertComponentModule,
    busyIndicatorComponentModule,
    trackerServiceModule,
    messageHubServiceModule,
  ])

  .factory(
    'challengeSubmittingDialog',
    function challengeSubmittingDialog($modal) {
      let dialog = null;

      function close() {
        if (dialog) {
          dialog.dismiss();
        }
        dialog = null;
      }

      return {
        close,
        /**
         * Opens the challenge submitting dialog and submits the challenge. Handles warnings and feedback
         * @param {Solution} props.solution
         * @param {Function} props.saveSolution
         * @param {Function} props.afterSubmit
         * @param {boolean} props.expired
         * @param {boolean} props.warn
         * @param {string} props.nextLabel
         * @param {string} props.onlyShowResults
         * @returns {Promise<boolean>} Returns a promise that rejects if the user changed their mind, and resolves
         * true if they'd like to continue editing the solution, or false if we should continue
         */
        open(props) {
          close();

          dialog = $modal.openOnce('challengeSubmittingDialog', {
            template: challengeSubmittingDialogTemplate,
            controller: 'ChallengeSubmittingDialogCtrl as $ctrl',
            backdrop: props.onlyShowResults ? true : 'static',
            windowClass: 'challenge-submitting-dialog',
            keyboard: !props.onlyShowResults,
            resolve: {
              props: () => props,
            },
          });

          return dialog.result.finally(() => {
            dialog = null;
          });
        },
      };
    },
  )

  .controller(
    'ChallengeSubmittingDialogCtrl',
    function ChallengeSubmittingDialogCtrl(
      $modalInstance,
      $scope,
      $q,
      props,
      timerHelper,
      appAlert,
      $timeout,
      Solution,
      tracker,
      messageHubService,
      $log,
    ) {
      const ctrl = this;
      const {
        solution,
        saveSolution,
        afterSubmit,
        expired,
        warn,
        nextLabel,
        onlyShowResults,
      } = props;
      const { longTimeout, runEvery } = timerHelper;

      ctrl.solution = solution;
      ctrl.issues = solution.updateSubmissionFailedMessages();
      ctrl.failedTests = () => ctrl.issues.length;
      ctrl.editIsPrimary = () => ctrl.failedTests() && ctrl.canEdit();
      ctrl.skipTooltip = () =>
        ctrl.editIsPrimary()
          ? `Only move forward if you don't believe you can improve on your solution!`
          : '';
      ctrl.nextLabel = nextLabel;
      ctrl.complete = $modalInstance.close;
      ctrl.cancel = $modalInstance.dismiss;
      const initialRemaining = solution.remainingAttempts();
      ctrl.hasLimits = initialRemaining < Infinity;
      ctrl.canEdit = () => ctrl.remaining !== 0 && !expired;

      // Listen for auth messages from the embedded ui-modern iframe to capture the Mastra token
      window.addEventListener('message', function (event) {
        const data = event && event.data;
        if (!data || data.type !== 'mastra-auth') {
          return;
        }

        try {
          if (data.token) {
            localStorage.setItem('MASTRA_CHAT_TOKEN', data.token);
          }
        } catch (e) {
          $log.error('Failed to capture Mastra token', e);
        }
      });

      if (onlyShowResults || !ctrl.canEdit) {
        // shows the previous results, if we have any
        ctrl.onlyShowResults = onlyShowResults;
      } else if (
        (initialRemaining === 1 && !expired) ||
        (initialRemaining !== Infinity && warn)
      ) {
        ctrl.showWarning = true;
        ctrl.remaining = initialRemaining;
        ctrl.startSubmission = startSubmission;
      } else {
        startSubmission();
      }

      $timeout(() => (ctrl.dialogReady = true));

      const startTime = Date.now();

      const trackData = (props = {}) => {
        const time = Math.floor((Date.now() - startTime) / 1000);
        const assessment = solution.assessment || {};
        return {
          id: solution.id,
          value: time, // for GA
          time,
          challengeId: solution.challenge.id,
          solutionId: solution.id,
          assessmentId: solution.assessmentId,
          assessmentTitle: assessment.title,
          teamCandidateId: solution.teamCandidateId,
          teamId: solution.teamId,
          team: assessment.team && assessment.team.name,
          ...props,
        };
      };

      function archiveMastraConversation() {
        try {
          const token = localStorage.getItem('MASTRA_CHAT_TOKEN');

          $log.info(
            'Archiving Mastra conversation',
            solution.id,
            MASTRA_BASE_URL,
            token,
          );

          if (!MASTRA_BASE_URL || !token || !solution || !solution.id) {
            return;
          }

          const url = `${MASTRA_BASE_URL}/solutions/${solution.id}/archive-conversation`;

          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }).catch(() => {
            $log.error('Failed to request archive Mastra conversation');
          });
        } catch (e) {
          $log.error('Failed to archive Mastra conversation', e);
        }
      }

      function startSubmission() {
        ctrl.inProgress = true;
        ctrl.queuing = true;
        ctrl.showWarning = false;
        // we'll attempt to save up to 4 times if we get an already submitting error, waiting 15s
        // between each attempt. After the last one, we just give up and let the candidate continue.
        let submissions = 0;
        const tryToSubmit = () => {
          submissions++;
          return Solution.submit(solution, {
            data: {
              submissionHash: solution.generateSubmissionHash(),
            },
          }).catch((resp) => {
            if (
              resp &&
              resp.status === 403 &&
              resp.data &&
              /already submitting/.test(resp.data.reason)
            ) {
              if (submissions > 3) {
                return $q.resolve();
              } else {
                tracker.trackError(
                  'Challenge Submit Reattempt',
                  trackData({ submissions }),
                );
                return $timeout(tryToSubmit, 15000);
              }
            }
            messageHubService.postError(ERROR_CONTEXT.submitSolution, resp);
            return appAlert.modelError(resp);
          });
        };

        saveSolution.afterSave
          .then(tryToSubmit)
          .then(waitOnSubmission)
          .catch($modalInstance.dismiss);
      }

      const submissionFeedback = solution.challenge.submissionFeedback;

      function waitOnSubmission() {
        ctrl.queuing = false;
        const submissionStartTime = Date.now();

        const errorTimeout = longTimeout(
          () => {
            if (!solution.isSubmitted()) {
              tracker.trackError('Challenge Submit Timeout', trackData());
              // TODO: is there a better way to handle this?
              $modalInstance.dismiss();
              appAlert.error(
                'There was an error finishing the submission of your solution, please refresh the page and try again.',
              );
            }
          },
          15 * 60 * 1000,
        ); // wait up to 15 minutes

        let autoRedirectTimer;
        let manualRefreshTimer;
        let alreadyHandledSubmitted = false;

        function checkSubmitted(source = 'unknown') {
          const elapsedTime = Date.now() - submissionStartTime;

          if (!alreadyHandledSubmitted && solution.isSubmitted()) {
            const likelySocket =
              elapsedTime < 25000 && source === 'socket-watch';

            clearTimers();
            solution.DSRefresh().finally(() => {
              alreadyHandledSubmitted = true;
              tracker.trackEvent(
                'Challenge Submit Completed',
                trackData({
                  detectionMethod: source,
                  detectionTimeMs: elapsedTime,
                  likelySocket,
                }),
              );
              archiveMastraConversation();
              afterSubmit();
              ctrl.submissionFailure =
                solution.isSubmissionInvalid() ||
                solution.isSubmissionTimeout();
              if (submissionFeedback || ctrl.submissionFailure) {
                ctrl.submissionInvalid = solution.isSubmissionInvalid();
                ctrl.submissionTimeout = solution.isSubmissionTimeout();
                ctrl.remaining = solution.remainingAttempts();
                ctrl.attemptNotUsed =
                  ctrl.hasLimits && ctrl.remaining === initialRemaining;
                ctrl.issues = solution.updateSubmissionFailedMessages();
                ctrl.inProgress = false;
              } else {
                ctrl.complete();
              }
            });
          }
        }

        const reloadSolution = () => {
          solution
            .DSRefresh({ ignoreLoadingBar: true })
            .then(() => checkSubmitted('manual-refresh'));
        };

        function clearTimers() {
          longTimeout.cancel(errorTimeout);
          runEvery.cancel(autoRedirectTimer);
          runEvery.cancel(manualRefreshTimer);
        }

        $scope.$on('$destroy', clearTimers);

        $scope.$watch(
          () => solution.isSubmitted(),
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              checkSubmitted('socket-watch');
            }
          },
        );

        // Also always manually refresh every 30s in case sockets aren't working correctly
        manualRefreshTimer = runEvery(reloadSolution, 30 * 1000);

        $timeout(() => checkSubmitted('initial-timeout'), 1000);
      }
    },
  );
